import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, FileText, Loader2, Phone, Mail, MapPin, Building2, User } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get(`/customers/${id}`), api.get(`/customers/${id}/quotes`)])
      .then(([c, q]) => { setCustomer(c.data); setQuotes(q.data); })
      .catch(() => navigate("/musteriler"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;
  if (!customer) return null;

  const info = [
    { icon: Phone, val: customer.phone },
    { icon: Mail, val: customer.email },
    { icon: MapPin, val: customer.address },
  ].filter((i) => i.val);

  return (
    <div>
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate("/musteriler")} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Müşteriler
      </Button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card className="p-6 border-slate-200">
            <div className="w-12 h-12 rounded-md bg-black text-white flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6" />
            </div>
            <h1 className="font-heading text-2xl font-black tracking-tight">{customer.company_name || customer.authorized_name}</h1>
            {customer.authorized_name && customer.company_name && (
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                <User className="w-4 h-4" /> {customer.authorized_name} {customer.authorized_title && `· ${customer.authorized_title}`}
              </p>
            )}
            <div className="mt-5 space-y-3">
              {info.map((i, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                  <i.icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <span>{i.val}</span>
                </div>
              ))}
            </div>
            {customer.tax_number && (
              <div className="mt-5 pt-4 border-t border-slate-100 text-sm">
                <p className="text-slate-500">Vergi Dairesi: <span className="text-gray-900 font-medium">{customer.tax_office}</span></p>
                <p className="text-slate-500">Vergi No: <span className="text-gray-900 font-medium">{customer.tax_number}</span></p>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-5">Ekleyen personel: <span className="font-medium text-gray-600">{customer.created_by_name || "-"}</span></p>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold">Teklifler ({quotes.length})</h2>
            <Link to={`/teklifler/yeni?customer=${customer.id}`}>
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="new-quote-for-customer">
                <Plus className="w-4 h-4 mr-2" /> Teklif Oluştur
              </Button>
            </Link>
          </div>
          <Card className="border-slate-200 divide-y divide-gray-100">
            {quotes.length === 0 && (
              <div className="p-10 text-center">
                <FileText className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-slate-500">Bu müşteriye ait teklif yok.</p>
              </div>
            )}
            {quotes.map((q) => (
              <Link key={q.id} to={`/teklifler/${q.id}`} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-testid={`customer-quote-${q.id}`}>
                <div>
                  <p className="font-semibold text-sm">{q.quote_number}</p>
                  <p className="text-xs text-slate-500">{(q.created_at || "").slice(0, 10)} · Hazırlayan: {q.prepared_by_name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-heading font-bold text-sm">{money(q.grand_total, q.currency)}</span>
                  <Badge className={q.status === "sent" ? "bg-rose-600" : ""} variant={q.status === "sent" ? "default" : "secondary"}>
                    {q.status === "sent" ? "Gönderildi" : "Taslak"}
                  </Badge>
                </div>
              </Link>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
